import type { Client, Measurement, Task } from "@coachapp/shared";
import {
  Activity,
  Archive,
  Calendar,
  Check,
  ChevronDown,
  ClipboardList,
  Ellipsis,
  FileText,
  Package,
  Pencil,
  Plus,
  RotateCcw,
  Ruler,
  Scale,
  Trash2,
  Trophy,
} from "lucide-react";
import { useLayoutEffect, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import ClientChallengesPanel from "../components/ClientChallengesPanel";
import ClientCredentialsBlock from "../components/ClientCredentialsBlock";
import ClientCrmPanel from "../components/ClientCrmPanel";
import ClientPackagesPanel from "../components/ClientPackagesPanel";
import ErrorAlert from "../components/ErrorAlert";
import { ScreenHeader } from "../components/layout/ScreenHeader";
import TrainingPlansPanel from "../components/TrainingPlansPanel";
import { useTransitionNavigate } from "../hooks/useTransitionNavigate";
import { getApiErrorMessage } from "../lib/api-error";
import { applySectionScrollFromUrl, getSectionScrollTarget } from "../lib/scroll-intent";
import { sortTasks } from "../lib/task-sort";
import { getClientStatusBadgeClass, isArchivedClient } from "../lib/client-status";
import {
  activePackageRemaining,
  MORE_SECTION_IDS,
  nearestActiveBooking,
  parseClientTab,
  type ClientTab,
} from "../lib/client-screen";
import { useAuth } from "../store/auth";
import {
  coachApi,
  useCompleteTaskMutation,
  useDeleteMeasurementMutation,
  useGetClientQuery,
  useGetMeasurementsQuery,
  useGetTasksQuery,
  useUpdateClientMutation,
} from "../store/coachApi";
import { useAppDispatch, useAppSelector } from "../store/hooks";

const selectBookings = coachApi.endpoints.getBookings.select(undefined);

const ClientDetailsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useTransitionNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const isTrainer = user?.role === "TRAINER";

  const {
    data: clientInfo,
    error: clientError,
    isLoading: clientLoading,
    refetch: refetchClient,
  } = useGetClientQuery({ id: id! }, { skip: !id });

  const { data: measurements = [] } =
    useGetMeasurementsQuery({ clientId: id! }, { skip: !id });

  const { data: tasks = [] } = useGetTasksQuery(
    { clientId: id! },
    { skip: !id || !isTrainer },
  );

  const [deleteMeasurement, { isLoading: deleting }] =
    useDeleteMeasurementMutation();
  const [completeTask] = useCompleteTaskMutation();
  const [updateClient, { isLoading: updatingClient }] = useUpdateClientMutation();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const isArchived = clientInfo ? isArchivedClient(clientInfo) : false;

  const history = useMemo(
    () =>
      [...measurements].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      ),
    [measurements],
  );

  const clientTasks = useMemo(() => sortTasks(tasks).slice(0, 3), [tasks]);

  const tab = parseClientTab(location.search);
  const section = getSectionScrollTarget(location.search);

  const bookings = useAppSelector((state) => selectBookings(state).data) ?? [];
  const selectPackages = useMemo(
    () => (id ? coachApi.endpoints.getClientPackages.select({ clientId: id }) : null),
    [id],
  );
  const packages = useAppSelector((state) =>
    selectPackages ? selectPackages(state).data : undefined,
  );

  const nextBooking = id ? nearestActiveBooking(bookings, id, new Date()) : null;
  const remainingSessions = activePackageRemaining(packages);
  const headerContext = [
    nextBooking
      ? new Date(nextBooking.startsAt).toLocaleString(undefined, {
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null,
    remainingSessions !== null
      ? t("{{count}} занятий", { count: remainingSessions })
      : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  const clientTabs: Array<{ key: ClientTab; label: string; icon: typeof ClipboardList }> = [
    { key: "plan", label: t("План"), icon: ClipboardList },
    { key: "packages", label: t("Пакеты"), icon: Package },
    { key: "challenges", label: t("Челленджи"), icon: Trophy },
    { key: "more", label: t("Ещё"), icon: Ellipsis },
  ];

  const switchTab = (next: ClientTab) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", next);
    params.delete("section");
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  };

  const toggleMoreSection = (sectionId: string) => {
    const currentlyOpen = openSections[sectionId] === true;
    if (currentlyOpen) {
      setOpenSections((prev) => ({ ...prev, [sectionId]: false }));
      if (section === sectionId) {
        const params = new URLSearchParams(location.search);
        params.delete("section");
        params.set("tab", "more");
        const qs = params.toString();
        navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
      }
      return;
    }
    setOpenSections((prev) => ({ ...prev, [sectionId]: true }));
  };

  const hasCredentials = Boolean(clientInfo?.login && clientInfo?.plainPassword);

  const fetchError = clientError
    ? getApiErrorMessage(clientError, "Не удалось загрузить данные клиента")
    : null;

  useEffect(() => {
    if (!section || !MORE_SECTION_IDS.has(section)) return;
    setOpenSections((prev) =>
      prev[section] === undefined ? { ...prev, [section]: true } : prev,
    );
  }, [section]);

  useEffect(() => {
    if (!isTrainer || !section || !MORE_SECTION_IDS.has(section)) return;
    const params = new URLSearchParams(location.search);
    if (params.get("tab") === "more") return;
    params.set("tab", "more");
    const qs = params.toString();
    navigate(`${location.pathname}?${qs}`, { replace: true });
  }, [isTrainer, section, location.pathname, location.search, navigate]);

  useLayoutEffect(() => {
    if (clientLoading || !isTrainer) return;
    applySectionScrollFromUrl(location.pathname, location.search);
  }, [clientLoading, isTrainer, location.pathname, location.search]);

  useEffect(() => {
    if (clientLoading || !isTrainer) return;
    if (!getSectionScrollTarget(location.search)) return;

    const frame = requestAnimationFrame(() => {
      applySectionScrollFromUrl(location.pathname, location.search);
    });
    return () => cancelAnimationFrame(frame);
  }, [clientLoading, isTrainer, location.pathname, location.search]);

  const handleCrmUpdate = (updated: Client) => {
    if (!id) return;
    dispatch(
      coachApi.util.updateQueryData('getClient', { id }, (draft) => {
        Object.assign(draft, updated);
      }),
    );
  };

  const handleCompleteTask = async (task: Task) => {
    if (task.status === "DONE") return;

    setCompletingTaskId(task.id);
    setActionError(null);
    try {
      await completeTask(task.id).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Ошибка при сохранении"));
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleDeleteMeasurement = async (measurementId: string) => {
    if (!confirm(t("Вы уверены, что хотите удалить этот замер?"))) {
      return;
    }

    setDeletingId(measurementId);
    try {
      await deleteMeasurement({ id: measurementId, clientId: id! }).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось удалить замер"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleArchiveClient = async () => {
    if (!id || !confirm(t("Вы уверены, что хотите архивировать этого клиента?"))) {
      return;
    }

    setActionError(null);
    try {
      await updateClient({ id, data: { status: "ARCHIVED" } }).unwrap();
      navigate("/clients");
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось архивировать клиента"));
    }
  };

  const handleRestoreClient = async () => {
    if (
      !id ||
      !confirm(t("Вы уверены, что хотите восстановить клиента из архива?"))
    ) {
      return;
    }

    setActionError(null);
    try {
      await updateClient({ id, data: { status: "INACTIVE" } }).unwrap();
      void refetchClient();
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось восстановить клиента"));
    }
  };

  const tasksBlock = (
    <div className="card p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h2 className="text-lg font-bold text-slate-900">
          {t("Задачи клиента")}
        </h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => navigate(`/tasks/new?clientId=${id}`)}
            className="btn-primary shrink-0 text-sm py-2 w-full sm:w-auto min-h-11"
          >
            <Plus size={16} />
            <span>{t("Создать задачу")}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate(`/tasks?clientId=${id}`)}
            className="btn-secondary shrink-0 text-sm py-2 w-full sm:w-auto min-h-11"
          >
            {t("Все задачи")}
          </button>
        </div>
      </div>
      {clientTasks.length === 0 ? (
        <p className="text-sm text-slate-500">{t("Нет задач")}</p>
      ) : (
        <div className="space-y-2">
          {clientTasks.map((task) => {
            const isDone = task.status === "DONE";

            return (
            <div
              key={task.id}
              className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0"
            >
              <button
                type="button"
                onClick={() => void handleCompleteTask(task)}
                disabled={isDone || completingTaskId === task.id}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                  isDone
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : "border-slate-300 hover:border-primary"
                }`}
                aria-label={t("Отметить выполненной")}
              >
                {completingTaskId === task.id ? (
                  <div className="w-2.5 h-2.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                ) : isDone ? (
                  <Check size={12} />
                ) : null}
              </button>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium truncate ${
                    isDone
                      ? "line-through text-slate-400"
                      : "text-slate-900"
                  }`}
                >
                  {task.title}
                </p>
                {task.dueAt && (
                  <p className="text-xs text-slate-500">
                    {new Date(task.dueAt).toLocaleDateString(
                      undefined,
                      {
                        day: "numeric",
                        month: "short",
                      },
                    )}
                  </p>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const measurementsBlock = (
    <>
      <div className="mb-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-bold text-slate-900">
          {t("История замеров")}
        </h2>
        <button
          type="button"
          onClick={() => navigate("measurements/new")}
          className="btn-primary shrink-0 self-start text-sm py-2 sm:self-auto"
        >
          <Plus size={18} />
          <span>{t("Добавить")}</span>
        </button>
      </div>

      {history.length === 0 ? (
        <div className="empty-state p-8 sm:p-12">
          <Activity size={48} className="mb-4 opacity-20" />
          <p className="font-medium">{t("История замеров пуста")}</p>
          <p className="text-sm mt-1">
            {t("Начните с добавления первого замера вашего клиента")}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {history.map((m: Measurement) => (
            <div key={m.id} className="card p-5 md:p-6 group">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <div className="flex items-center gap-2 text-slate-500 min-w-0">
                  <Calendar size={16} className="shrink-0" />
                  <span className="text-sm font-semibold text-slate-500">
                    {new Date(m.date).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                  {isTrainer && (
                    <>
                      <button
                        onClick={() =>
                          navigate(`measurements/${m.id}/edit`)
                        }
                        className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-primary"
                        title={t("Редактировать")}
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteMeasurement(m.id)}
                        disabled={deletingId === m.id || deleting}
                        className="p-2 hover:bg-red-50 rounded-xl transition-colors text-slate-400 hover:text-red-500 disabled:opacity-50"
                        title={t("Удалить")}
                      >
                        {deletingId === m.id ? (
                          <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                {m.weight && (
                  <div className="bg-slate-50 p-3 rounded-2xl">
                    <div className="flex items-center gap-2 text-slate-400 mb-1">
                      <Scale size={14} />
                      <span className="section-label">
                        {t("Вес")}
                      </span>
                    </div>
                    <div className="text-lg font-bold text-slate-900">
                      {m.weight}{" "}
                      <span className="text-xs font-medium text-slate-500">
                        {t("кг")}
                      </span>
                    </div>
                  </div>
                )}
                {m.height && (
                  <div className="bg-slate-50 p-3 rounded-2xl">
                    <div className="flex items-center gap-2 text-slate-400 mb-1">
                      <Ruler size={14} />
                      <span className="section-label">
                        {t("Рост")}
                      </span>
                    </div>
                    <div className="text-lg font-bold text-slate-900">
                      {m.height}{" "}
                      <span className="text-xs font-medium text-slate-500">
                        {t("см")}
                      </span>
                    </div>
                  </div>
                )}
                {m.age && (
                  <div className="bg-slate-50 p-3 rounded-2xl">
                    <div className="flex items-center gap-2 text-slate-400 mb-1">
                      <Calendar size={14} />
                      <span className="section-label">
                        {t("Возраст")}
                      </span>
                    </div>
                    <div className="text-lg font-bold text-slate-900">
                      {m.age}{" "}
                      <span className="text-xs font-medium text-slate-500">
                        {t("лет")}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {m.analysesText && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-slate-400 mb-1.5">
                    <FileText size={14} />
                    <span className="section-label">
                      {t("Анализы")}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 font-medium leading-relaxed">
                    {m.analysesText}
                  </p>
                </div>
              )}

              {m.trainerComment && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-primary mb-1.5">
                    <FileText size={14} />
                    <span className="micro-label text-primary">
                      {t("Комментарий для клиента")}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 font-medium leading-relaxed">
                    {m.trainerComment}
                  </p>
                </div>
              )}

              {m.notes && (
                <div className="mt-3 text-xs text-slate-400 italic">
                  {m.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  const archiveBlock = clientInfo ? (
    <div className="flex flex-col items-center text-center">
      {isArchived ? (
        <>
          <p className="text-sm text-slate-600 mb-4">{t("Клиент в архиве")}</p>
          <button
            type="button"
            onClick={handleRestoreClient}
            disabled={updatingClient}
            className="btn-secondary text-sm py-2 inline-flex items-center gap-2"
          >
            {updatingClient ? (
              <div className="w-4 h-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            ) : (
              <RotateCcw size={16} />
            )}
            <span>{t("Восстановить из архива")}</span>
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={handleArchiveClient}
          disabled={updatingClient}
          className="btn-secondary text-sm py-2 inline-flex items-center gap-2 text-red-600 hover:text-red-700"
        >
          {updatingClient ? (
            <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
          ) : (
            <Archive size={16} />
          )}
          <span>{t("Архивировать")}</span>
        </button>
      )}
    </div>
  ) : null;

  const moreSections: Array<{ id: string; label: string; content: ReactNode }> = [
    { id: "client-tasks", label: t("Задачи"), content: tasksBlock },
    { id: "client-measurements", label: t("Замеры"), content: measurementsBlock },
    {
      id: "client-notes",
      label: t("Заметки"),
      content: clientInfo ? (
        <ClientCrmPanel client={clientInfo} onUpdate={handleCrmUpdate} />
      ) : null,
    },
    ...(hasCredentials && clientInfo?.login && clientInfo?.plainPassword
      ? [
          {
            id: "client-credentials",
            label: t("Доступ в приложение"),
            content: (
              <ClientCredentialsBlock
                login={clientInfo.login}
                password={clientInfo.plainPassword}
              />
            ),
          },
        ]
      : []),
  ];

  const renderMoreSection = (moreSection: { id: string; label: string; content: ReactNode }) => {
    const isOpen = openSections[moreSection.id] === true;
    return (
      <div key={moreSection.id} className="border-b border-slate-200 pb-4">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => toggleMoreSection(moreSection.id)}
          className="flex w-full items-center justify-between gap-2 text-left"
        >
          <span className="text-lg font-bold text-slate-900">{moreSection.label}</span>
          <ChevronDown
            size={20}
            className={`shrink-0 text-slate-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {isOpen && (
          <div id={moreSection.id} className="mt-4 min-w-0">
            {moreSection.content}
          </div>
        )}
      </div>
    );
  };

  if (clientLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner h-12 w-12" />
      </div>
    );
  }

  if (fetchError && !clientInfo) {
    return (
      <div className="page-container max-w-[800px] pb-20 space-y-4">
        <ScreenHeader
          title={t("Клиент")}
          onBack={() => navigate(-1)}
          backLabel={t("Назад")}
        />
        <ErrorAlert message={fetchError} onRetry={() => void refetchClient()} />
      </div>
    );
  }

  return (
    <>
      <div className="page-container max-w-[800px] pb-20 min-w-0">
        {actionError && (
          <ErrorAlert
            message={actionError}
            onDismiss={() => setActionError(null)}
            className="mb-4"
          />
        )}
        <ScreenHeader
          title={clientInfo?.name || t("Клиент")}
          onBack={() => navigate(-1)}
          backLabel={t("Назад")}
          subtitle={
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={getClientStatusBadgeClass(clientInfo?.status ?? "INACTIVE")}>
                {t(`clientStatus.${clientInfo?.status}`, {
                  defaultValue: clientInfo?.status,
                })}
              </span>
              {headerContext && (
                <>
                  <span className="text-slate-400 text-xs font-medium hidden sm:inline">
                    •
                  </span>
                  <span className="text-slate-500 text-xs font-medium truncate max-w-full">
                    {headerContext}
                  </span>
                </>
              )}
              {clientInfo?.email && (
                <>
                  <span className="text-slate-400 text-xs font-medium hidden sm:inline">
                    •
                  </span>
                  <span className="text-slate-500 text-xs font-medium truncate max-w-full">
                    {clientInfo.email}
                  </span>
                </>
              )}
            </div>
          }
        />

        {id && isTrainer && (
          <div className="flex flex-col sm:flex-row gap-2 mb-6">
            <button
              type="button"
              onClick={() =>
                navigate("/bookings/new", { state: { clientId: id } })
              }
              className="btn-primary text-sm py-2.5 w-full sm:w-auto min-h-11"
            >
              <Calendar size={16} />
              <span>{t("Записать на тренировку")}</span>
            </button>
          </div>
        )}

        <div className="grid gap-4 sm:gap-6 min-w-0">
          {isTrainer ? (
            <>
              <div className="@container">
                <div className="segmented grid-cols-4">
                  {clientTabs.map((clientTab) => {
                    const Icon = clientTab.icon;
                    return (
                      <button
                        key={clientTab.key}
                        type="button"
                        aria-pressed={tab === clientTab.key}
                        aria-label={clientTab.label}
                        onClick={() => switchTab(clientTab.key)}
                        className={`segmented-item ${
                          tab === clientTab.key
                            ? "segmented-item-active"
                            : "segmented-item-idle"
                        }`}
                      >
                        <Icon size={18} aria-hidden className="@[28rem]:hidden" />
                        <span className="hidden @[28rem]:inline">{clientTab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {tab === "plan" && id && <TrainingPlansPanel clientId={id} />}

              {tab === "packages" && id && (
                <div id="client-packages">
                  <ClientPackagesPanel clientId={id} />
                </div>
              )}

              {tab === "challenges" && id && <ClientChallengesPanel clientId={id} />}

              {tab === "more" && (
                <>
                  {moreSections.map((moreSection) => renderMoreSection(moreSection))}
                  {archiveBlock}
                </>
              )}
            </>
          ) : (
            measurementsBlock
          )}
        </div>
      </div>
    </>
  );
};

export default ClientDetailsPage;
